import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, AlertMessage } from "components/atoms";
import { TextInput } from "components/molecules";
import { useAuth } from "contexts";

const ConfirmSignInSchema = Yup.object().shape({
  confirmTotpCode: Yup.string()
    .matches(/^\d+$/, "TOTP code must contain only numbers")
    .length(6, "TOTP code must be exactly 6 digits")
    .required("TOTP code is required"),
});

const ConfirmSignInForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthState, handleSignInConfirmation } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSuccessfulConfirmation = (redirectPath?: string): void => {
    confirmSignInFormik.resetForm();

    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  };

  const handleConfirmationError = (error: unknown): void => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    if (errorMessage.includes("Access Denied.")) {
      setErrorMessage("Your access is either invalid or has not yet been confirmed. Please reach out to your system administrator for support.");
    } else {
      setErrorMessage(errorMessage);
    }
  };

  const handleConfirmSignInSubmit = async (values: {
    confirmTotpCode: string;
  }) => {
    try {
      const { redirectAfterSignUp } = location.state || {};

      const confirmSignInError = await handleSignInConfirmation(
        values.confirmTotpCode.toString()
      );

      if (confirmSignInError) {
        handleConfirmationError(confirmSignInError);
        return;
      }

      handleSuccessfulConfirmation(redirectAfterSignUp);
    } catch (error) {
      handleConfirmationError(error);
    }
  };

  const confirmSignInFormik = useFormik({
    initialValues: {
      confirmTotpCode: "",
    },
    validationSchema: ConfirmSignInSchema,
    onSubmit: handleConfirmSignInSubmit,
  });

  const toSignIn = (): void => {
    setAuthState((prevState) => ({
      ...prevState,
      nextStep: "SIGN_IN",
    }));
  };

  return (
    <form className="auth-form" onSubmit={confirmSignInFormik.handleSubmit}>
      <div className="form-group">
        <h2>Enter Your Authenticator Code</h2>
        <small>
          To keep your account safe, please open your authenticator app (Google
          Authenticator, Microsoft Authenticator, etc.) and enter the 6-digit
          code. This code is generated by your app, <b>not sent via email</b>,
          and changes every 30 seconds.
        </small>
      </div>

      <TextInput
        label="Code *"
        id="confirmTotpCode"
        placeholder="Enter the TOTP code"
        inputMode="numeric"
        maxLength={6}
        getFieldProps={confirmSignInFormik.getFieldProps}
        touched={confirmSignInFormik.touched.confirmTotpCode}
        error={confirmSignInFormik.errors.confirmTotpCode}
      />

      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}

      <Button
        type="submit"
        text="Confirm"
        className="form-button"
        width="100%"
        variant="primary"
      />

      <Button
        type="button"
        text="Back to Sign In"
        onClick={toSignIn}
        className="form-button"
        width="100%"
        variant="border"
      />
    </form>
  );
};

export default ConfirmSignInForm;
